import React, { FC, useState } from 'react';
import { Field } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { MenuItem } from '@material-ui/core';
import { SelectAdapter, TextFieldAdapter, validations } from '../../../../shared/components';
import { ExerciseFormInitialValues, ExerciseFormMode, ExerciseFormType } from '../../../types';
import { FlexFormControl } from './Atoms';
import { OptionalFormGroup } from './OptionalFormGroup';
import { BloomType } from '../../../../../config/api/models/Exercise';
import { useParams } from 'react-router';
import { UrlParams } from '../../../../shared/types';
import { useUser } from '../../../../shared';
import { FieldValidator } from 'final-form';
import WysiwygEditorField from './components/WysiwygEditorField';

type Props = {
  mode?: ExerciseFormMode;
  type?: ExerciseFormType;
  isAlgebraKitSelected?: boolean;
};

const fieldsSubscription = {
  touched: true,
  error: true,
  value: true,
  valid: true,
  submitError: true,
  hasSubmitErrors: true,
  submitFailed: true,
  submitSucceeded: true,
  submitting: true,
};

const ExerciseFormContentFields: FC<Props> = ({
  mode = 'normal',
  type = 'add',
  isAlgebraKitSelected = false,
}) => {
  const { t } = useTranslation();
  const { seriesId } = useParams<UrlParams>();
  const { isAuthor } = useUser();

  const [isTitleAndDescripitionRequired, setIsTitleAndDescripitionRequired] =
    useState<boolean>(false);

  const validateDescription: FieldValidator<string> = (values, allValues, meta) => {
    const newAllValues: ExerciseFormInitialValues = allValues as ExerciseFormInitialValues;

    if (newAllValues.type === 'text_exercise' && meta?.name === 'description' && values === '') {
      setIsTitleAndDescripitionRequired(true);
      return validations(t).required;
    }

    return undefined;
  };

  return (
    <>
      <OptionalFormGroup enabled={mode === 'normal'}>
        <FlexFormControl
          label={t('shared.forms.title')}
          flex={mode === 'normal'}
          grow={1}
          id="title"
          required={seriesId != null || isTitleAndDescripitionRequired}
        >
          <>
            <Field
              name="title"
              validate={
                seriesId != null || isTitleAndDescripitionRequired
                  ? validations(t).required
                  : undefined
              }
              component={TextFieldAdapter}
              subscription={fieldsSubscription}
            />
          </>
        </FlexFormControl>
        <FlexFormControl
          label={'Beschrijving'}
          flex={mode === 'normal'}
          grow={1}
          id="description"
          required={isTitleAndDescripitionRequired}
        >
          <>
            <Field
              name="description"
              validate={validateDescription}
              subscription={fieldsSubscription}
              children={(props) => (
                <WysiwygEditorField props={props} required={isTitleAndDescripitionRequired} />
              )}
            />
          </>
        </FlexFormControl>
        {type === 'add' && (
          <FlexFormControl
            label={t('shared.forms.type')}
            flex={mode === 'normal'}
            grow={1}
            id="type"
            required
          >
            <Field
              name="type"
              component={SelectAdapter}
              validate={validations(t).required}
              subscription={fieldsSubscription}
            >
              <MenuItem value="h5p">{t('shared.forms.exerciseType.h5p')}</MenuItem>
              <MenuItem value="book_widget">{t('shared.forms.exerciseType.bookwidgets')}</MenuItem>
              {isAuthor && (
                <MenuItem value="algebra_kit">{t('shared.forms.exerciseType.algebrakit')}</MenuItem>
              )}
              <MenuItem value="text_exercise">Oefening via tekst</MenuItem>
            </Field>
          </FlexFormControl>
        )}
        {isAlgebraKitSelected && (
          <FlexFormControl
            label={t('shared.forms.algebraKitExerciseId')}
            flex={mode === 'normal'}
            grow={1}
            id="algebra_kit_exercise_id"
            required
          >
            <Field
              name="algebra_kit_exercise_id"
              component={TextFieldAdapter}
              validate={isAlgebraKitSelected ? validations(t).required : undefined}
              subscription={fieldsSubscription}
            />
          </FlexFormControl>
        )}
        <FlexFormControl
          label={t('shared.forms.bloom')}
          flex={mode === 'normal'}
          grow={1}
          id="bloom_type"
          required
        >
          <Field name="bloom_type" component={SelectAdapter} validate={validations(t).required}>
            {Object.values(BloomType).map((bloomType) => (
              <MenuItem key={bloomType} value={bloomType}>
                {t(`trajectory.sections.shared.type.${bloomType}`)}
              </MenuItem>
            ))}
          </Field>
        </FlexFormControl>
        <FlexFormControl
          label={t('shared.forms.difficulty')}
          flex={mode === 'normal'}
          grow={1}
          id="level"
          required
        >
          <Field name="level" component={SelectAdapter} validate={validations(t).required}>
            <MenuItem value="1">1 - {t('trajectory.sections.shared.level.one')}</MenuItem>
            <MenuItem value="2">2 - {t('trajectory.sections.shared.level.two')}</MenuItem>
            <MenuItem value="3">3 - {t('trajectory.sections.shared.level.three')}</MenuItem>
          </Field>
        </FlexFormControl>
        <FlexFormControl
          label={'Uitwerking'}
          flex={mode === 'normal'}
          grow={1}
          id="uitwerking"
          required
        >
          <Field name="exercise_tool" component={SelectAdapter} validate={validations(t).required}>
            <MenuItem value="1">Nog niet gekozen</MenuItem>
            <MenuItem value="fully_digital">Volledig digitaal</MenuItem>
            <MenuItem value="digital_with_paper">Digitaal met papier</MenuItem>
          </Field>
        </FlexFormControl>
      </OptionalFormGroup>
    </>
  );
};

export default ExerciseFormContentFields;
